import { useEffect, useRef } from "react";

/**
 * Consulta `fetcher` cada `intervalMs`, sin solapar peticiones y parando al
 * desmontar.
 *
 * Existe para reemplazar el patrón `useEffect(..., [lock])` que se auto-reinvoca:
 * al liberar el lock dentro del `.finally()`, el efecto se volvía a disparar sin
 * ninguna espera, dejando la petición en bucle cerrado contra la API. Un solo
 * detalle abierto llegaba a 5-20 req/s.
 *
 * Al `fetcher` se le pasa `isActive()` para que pueda evitar un setState después
 * de desmontar el componente.
 *
 * `resetKey` reinicia el ciclo y consulta de inmediato cuando cambia algo de lo
 * que depende el fetcher (un filtro, un id) sin esperar al siguiente intervalo.
 */
export default function usePolling(fetcher, intervalMs, enabled = true, resetKey = null) {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    let inFlight = false;

    const tick = () => {
      if (!active || inFlight) return;
      inFlight = true;
      Promise.resolve(fetcherRef.current(() => active)).finally(() => {
        inFlight = false;
      });
    };

    tick(); // primera carga sin esperar el intervalo
    const id = setInterval(tick, intervalMs);

    return () => {
      active = false;
      clearInterval(id);
    };
  }, [enabled, intervalMs, resetKey]);
}
